import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame } from "lucide-react";
import { CoverImage } from "@/components/CoverImage";
import { fmtNum } from "@/components/analytics/Widgets";
import { t } from "@/i18n";
import { Spotlight, ease } from "./motion";

const SEGMENTS = (staff) => [
  { key: "approved", label: "Aprobados", color: "hsl(var(--blue))" },
  { key: "completed", label: staff ? "En distribución" : "En revisión", color: "#22d3ee", keys: staff ? ["completed"] : ["completed", "failed"] },
  { key: "processing", label: "En proceso", color: "#f59e0b", keys: ["queued", "running", "uploading", "waiting_2fa"] },
  { key: "draft", label: "Borradores", color: "hsl(var(--muted-foreground))" },
  ...(staff ? [{ key: "failed", label: "Fallidos", color: "hsl(var(--primary))" }] : []),
];

export const Pipeline = ({ byStatus = {}, staff, top }) => {
  const segs = SEGMENTS(staff).map((s) => ({ ...s, n: (s.keys || [s.key]).reduce((a, k) => a + (byStatus[k] || 0), 0) })).filter((s) => s.n > 0 || ["approved", "draft"].includes(s.key));
  const total = segs.reduce((a, s) => a + s.n, 0) || 1;
  return (
    <Spotlight className="p-6 lg:col-span-4 flex flex-col" data-testid="pipeline-panel">
      <p className="eyebrow">{t("Estado del catálogo")}</p>
      <p className="font-display text-2xl font-bold mt-1">{t("{n} lanzamientos", { n: total === 1 && !segs.some((s) => s.n) ? 0 : total })}</p>
      <div className="flex h-3 rounded-full overflow-hidden bg-muted mt-4 gap-[2px]">
        {segs.map((s, i) => (
          <motion.div key={s.key} initial={{ flexGrow: 0, opacity: 0 }} animate={{ flexGrow: Math.max(s.n, 0.0001), opacity: 1 }} transition={{ duration: 0.9, ease, delay: 0.2 + i * 0.08 }}
            style={{ backgroundColor: s.color, flexBasis: 0 }} className="h-full first:rounded-l-full last:rounded-r-full" title={`${t(s.label)}: ${s.n}`} />
        ))}
      </div>
      <ul className="mt-4 space-y-2">
        {segs.map((s, i) => (
          <motion.li key={s.key} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.35 + i * 0.07 }} className="flex items-center justify-between text-sm" data-testid={`pipeline-${s.key}`}>
            <span className="flex items-center gap-2 text-muted-foreground"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />{t(s.label)}</span>
            <span className="font-medium tabular-nums">{s.n} <span className="text-xs text-muted-foreground">· {Math.round((s.n / total) * 100)}%</span></span>
          </motion.li>
        ))}
      </ul>
      {top && (
        <Link to={`/lanzamientos/${top.id}`} className="mt-auto pt-5 block" data-testid="top-release">
          <p className="eyebrow mb-2 flex items-center gap-1.5"><Flame className="h-3 w-3 text-primary" /> {t("Destacado de la semana")}</p>
          <div className="flex items-center gap-3 rounded-xl border border-border p-3 hover:border-primary/50 hover:bg-primary/5 transition-colors group">
            <CoverImage fileId={top.cover_file_id} className="h-12 w-12 shrink-0 transition-transform duration-500 group-hover:scale-105" />
            <div className="min-w-0 flex-1"><p className="text-sm font-semibold truncate">{top.title}</p><p className="text-xs text-muted-foreground truncate">{top.artist_name}{top.top_territory ? ` · ${top.top_territory}` : ""}</p></div>
            {top.social_views_7d > 0 && <div className="text-right shrink-0"><p className="font-display font-bold tabular-nums">{fmtNum(top.social_views_7d)}</p><p className="text-[10px] text-muted-foreground">{t("vistas 7d")}</p></div>}
          </div>
        </Link>
      )}
    </Spotlight>
  );
};
